import React, { useContext, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import { useSearchParams, useParams } from "../../hooks/useNavigation";
import PropTypes from "prop-types";

import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import useIsHydrated from "../../hooks/useIsHydrated";
import Loader from "../Loader/Loader";
import RoleFilterSelect from "../RoleFilterSelect/RoleFilterSelect";
import GridVacancyList from "./GridVacancyList";
import NoVacancies from "./NoVacancies";
import StyledPagination from "./StyledPagination";

const ITEMS_PER_PAGE = 12;

function GridSearchResults(props) {
  const { isLoading } = props;

  const {
    additionalConfig,
    displayVancanciesCount = true,
    itemsPerPage,
    jobCardType = "default",
  } = useContext(ComponentContext);

  const itemsToDisplay = itemsPerPage || ITEMS_PER_PAGE;
  const { filtersStore } = useStores();
  const { searchResults, frontendFilters, pagination, isFetching } =
    filtersStore;

  const [paginatedSearchResults, setPaginatedSearchResults] =
    useState(searchResults);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const searchParams = useSearchParams();

  const isHydrated = useIsHydrated();
  const isServer = !isHydrated;

  const filteredSearchResults = searchResults.filter(
    (result) =>
      isEmpty(frontendFilters.categories) ||
      frontendFilters.categories.includes(result.category)
  );

  useEffect(() => {
    const endOffset = currentPage + itemsToDisplay;

    setPaginatedSearchResults(
      filteredSearchResults.slice(currentPage, endOffset)
    );
    setPageCount(Math.ceil(filteredSearchResults.length / itemsToDisplay));
  }, [currentPage, itemsToDisplay, JSON.stringify(filteredSearchResults)]);

  const hasMounted = useRef(false);
  const searchParamsString = searchParams.toString();

  useEffect(() => {
    filtersStore.search().then(() => {
      hasMounted.current = true;
    });
    setCurrentPage(0);
  }, [searchParamsString]);

  const handlePageClick = (event) => {
    const newOffset =
      (event.selected * itemsToDisplay) % pagination.totalResults;
    setCurrentPage(newOffset);
  };

  const listClassName = classNames("col", {
    "col-md-9 col-lg-10": !additionalConfig?.disableRoleFilter,
  });

  const content = (
    <>
      <div className="mb-5">
        <GridVacancyList
          displayVancanciesCount={displayVancanciesCount}
          isLoading={isFetching}
          resultsCount={pagination.totalResults}
          searchResults={paginatedSearchResults}
          type={jobCardType}
        />
      </div>
      {pagination.totalResults > itemsToDisplay ? (
        <StyledPagination
          handlePageClick={handlePageClick}
          pageCount={pageCount}
        />
      ) : null}
    </>
  );

  const isEmptyResults = isEmpty(paginatedSearchResults);

  return (
    <div className="row gy-4">
      {!additionalConfig?.disableRoleFilter && (
        <div className="col-md-3 col-lg-2">
          <RoleFilterSelect isLoading={isFetching || isServer} />
        </div>
      )}
      <div className={listClassName}>
        {isLoading && <Loader minHeight="450px" />}
        {isEmptyResults && <NoVacancies />}
        {!isLoading && !isEmptyResults && content}
      </div>
    </div>
  );
}

export default observer(GridSearchResults);

GridSearchResults.propTypes = {
  isLoading: PropTypes.bool,
};

GridSearchResults.defaultProps = {
  isLoading: false,
};
